import { useState } from 'react';
import { Input } from '@/components/ui/FormControls';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import { useJerseyEntry } from '@/hooks/useJerseyEntry';
import { jerseyNameSuggestion, jerseyEntrySku, jerseyEntryPrefix } from '@klothiq/domain';
import type { ValidatedProductGroup } from '@/utils/bulkImportValidation';
import JerseyDetailsFields from './JerseyDetailsFields';
import JerseyGenerationMode from './JerseyGenerationMode';

function GroupOptions({
  profileId,
  group,
  onChange,
}: {
  profileId: string;
  group: ValidatedProductGroup;
  onChange: (group: ValidatedProductGroup) => void;
}) {
  const context = useJerseyEntry(profileId, group.existing_id);
  if (context.isLoading || context.isFetching)
    return <LoadingState message="Loading optional jersey details…" />;
  if (context.error)
    return <ErrorState message={context.error.message} onRetry={context.refetch} />;
  if (!context.data?.enabled)
    return (
      <p className="text-sm text-muted-foreground">
        Optional jersey entry is unavailable for this category.
      </p>
    );
  const entry = group.jersey_entry || {};
  const details = entry.details || context.data.current?.details || {};
  const name = jerseyNameSuggestion(details, context.data);
  const code =
    group.existing_id || (entry.code_mode || (group.product_code ? 'manual' : 'auto')) === 'manual'
      ? group.product_code
      : `${jerseyEntryPrefix(details, context.data)}-Assigned on save`;
  const change = (patch: Partial<NonNullable<ValidatedProductGroup['jersey_entry']>>) =>
    onChange({ ...group, jersey_entry: { ...entry, details, ...patch } });
  return (
    <div className="space-y-4 pt-3">
      <JerseyDetailsFields
        value={details}
        context={context.data}
        onChange={(details) => change({ details })}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <JerseyGenerationMode
            label={`${group.name} import name`}
            value={entry.name_mode || 'manual'}
            autoDisabled={!name}
            onChange={(name_mode) => change({ name_mode })}
          />
          <p className="break-words text-sm">
            Name: {entry.name_mode === 'auto' ? name : group.name}
          </p>
          {(entry.name_mode || 'manual') === 'manual' && (
            <Input
              value={group.name}
              maxLength={200}
              helpLabel={`${group.name} import product name`}
              onChange={(e) =>
                onChange({
                  ...group,
                  name: e.target.value,
                  jersey_entry: { ...entry, details, name_mode: 'manual' },
                })
              }
            />
          )}
        </div>
        {!group.existing_id && (
          <div className="space-y-2">
            <JerseyGenerationMode
              label={`${group.name} import code`}
              value={entry.code_mode || (group.product_code ? 'manual' : 'auto')}
              onChange={(code_mode) => change({ code_mode })}
            />
            <p className="break-all text-sm">
              Code: {code || 'Enter a code below, or choose Auto'}
            </p>
            {(entry.code_mode || (group.product_code ? 'manual' : 'auto')) === 'manual' && (
              <Input
                value={group.product_code}
                maxLength={160}
                helpLabel={`${group.name} import manual product code`}
                onChange={(e) =>
                  onChange({
                    ...group,
                    product_code: e.target.value.toUpperCase(),
                    jersey_entry: { ...entry, details, code_mode: 'manual' },
                  })
                }
              />
            )}
          </div>
        )}
      </div>
      <div className="space-y-3">
        {group.variants.map((v) => {
          const mode = entry.sku_modes?.[v.sourceRow] || (v.sku ? 'manual' : 'auto');
          let suggestion = '';
          try {
            suggestion = jerseyEntrySku(details, code, v.color || '', v.size, context.data!);
          } catch {
            /* Field errors are validated before commit. */
          }
          return (
            <div key={v.sourceRow} className="rounded-xl border border-border p-3 text-sm">
              <p className="mb-2 font-medium">
                Row {v.sourceRow} · {v.color || 'No colour'} / {v.size}
              </p>
              {!v.existing_id && (
                <JerseyGenerationMode
                  label={`Import row ${v.sourceRow} SKU`}
                  value={mode}
                  onChange={(sku_mode) =>
                    change({ sku_modes: { ...entry.sku_modes, [v.sourceRow]: sku_mode } })
                  }
                />
              )}
              <p className="mt-2 break-all font-mono text-xs">
                {v.existing_id || mode === 'manual'
                  ? v.sku || 'Enter a SKU below or choose Auto'
                  : suggestion}
              </p>
              {!v.existing_id && mode === 'manual' && (
                <Input
                  className="mt-2"
                  value={v.sku}
                  helpLabel={`Import row ${v.sourceRow} manual SKU`}
                  onChange={(e) =>
                    onChange({
                      ...group,
                      jersey_entry: {
                        ...entry,
                        details,
                        sku_modes: { ...entry.sku_modes, [v.sourceRow]: 'manual' },
                      },
                      variants: group.variants.map((row) =>
                        row.sourceRow === v.sourceRow ? { ...row, sku: e.target.value } : row,
                      ),
                    })
                  }
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
function EntryGroup(props: {
  profileId: string;
  group: ValidatedProductGroup;
  onChange: (group: ValidatedProductGroup) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="rounded-xl border border-border p-4"
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary className="min-h-11 cursor-pointer py-2 text-sm font-medium">
        {props.group.name} · Optional naming and SKU suggestions
      </summary>
      {open && <GroupOptions {...props} />}
    </details>
  );
}
export default function JerseyImportEntry({
  profileId,
  groups,
  onChange,
}: {
  profileId: string;
  groups: ValidatedProductGroup[];
  onChange: (key: string, group: ValidatedProductGroup) => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Names and supplied identifiers from your file stay Manual. Blank new codes/SKUs use Auto.
        Optional details and generation can be reviewed per group below.
      </p>
      {groups.map((group) => (
        <EntryGroup
          key={group.group_key}
          profileId={profileId}
          group={group}
          onChange={(next) => onChange(group.group_key, next)}
        />
      ))}
    </div>
  );
}
